import React, { useState, useEffect, useMemo, useRef } from 'react';
import { LatLngTuple, marker, LatLngBoundsLiteral } from 'leaflet';
import {
  FeatureGroup,
  LayersControl,
  Map,
  TileLayer,
  LayerGroup,
  GeoJSON,
  Viewport,
  Polyline,
  Popup,
  CircleMarker,
} from 'react-leaflet';

import { EditControl } from 'react-leaflet-draw';
import { Modal, Backdrop, Fade, CircularProgress, Chip } from '@material-ui/core';
import { Link } from 'react-router-dom';
import UTFGrid from '../../libs/leaflet-utfgrid/L.UTFGrid';
import { optionStyles } from './styles';
import { fortalezaGeoJSON } from './Fortaleza';
import api from '../../services/api';
import { toDate } from '../../utils/formatDate';
import { EducFilterOpsType, EngFilterOpsType, IncFilterOpsType, SurvFilterOpsType } from '../FilterBar';
import ShowSnackbar, { SetSnackbarType } from '../SnackBar';
import Colors from '../Colors';
import { fortalezaCansGeoJSON } from './FortalezaCans';
import CameraIcon from '../../assets/security-camera.png';
import { MapInformation } from '../../@types/records';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

const POSITION_SEARCH = {
  latlng: {
    lat: -3.8046721,
    lng: -38.5697856,
  },
};

const ZOOM_VALUE = 11.3;

export interface DrawPolygonType {
  polygon: {
    coordinates: Array<Array<[number, number]>>;
    type: 'Polygon' | '';
  };
}

interface IProps {
  content?: MapInformation[];
  pageName?: string;
  occurred_min: string;
  occurred_max: string;
  jsonb: object;
  polygon: React.Dispatch<React.SetStateAction<DrawPolygonType | {}>>;
  positionSearch: LatLngTuple;
  zoomSearch: number;
  polygon_id: {};
  bbox: LatLngBoundsLiteral;
  type: String;
  apiName: string;
  registerName: string;
  loadingMap: boolean;
  viewport?: Viewport;
  config: AxiosRequestConfig;
  fetchTileKeyRequest: Function;
}

const initialFilterOps: IncFilterOpsType | EngFilterOpsType | SurvFilterOpsType | EducFilterOpsType = {
  endDate: '',
  startDate: '',
  veiculos: [],
  gravidades: [],
  tipoAcidentes: [],
  bairros: '',
  status: [],
  tipo: [],
  especificacao: [],
  drawPolygon: {},
  focoMissao: [],
  tipoMissao: [],
};

const loadFilterStorage = (
  filterType: string,
): IncFilterOpsType | EngFilterOpsType | SurvFilterOpsType | EducFilterOpsType => {
  let parsedJsonbObj:
    | IncFilterOpsType
    | EngFilterOpsType
    | SurvFilterOpsType
    | EducFilterOpsType = initialFilterOps;
  let loadedFilterOps: null | string = null;
  if (filterType === 'sinistro') {
    loadedFilterOps = localStorage.getItem('incFilterOps');
  }
  if (filterType === 'sinistroSAMU') {
    loadedFilterOps = localStorage.getItem('incSAMUFilterOps');
  }
  if (filterType === 'engenharia') {
    loadedFilterOps = localStorage.getItem('engFilterOps');
  }
  if (filterType === 'fiscalizacao') {
    loadedFilterOps = localStorage.getItem('survFilterOps');
  }
  if (loadedFilterOps) {
    parsedJsonbObj = JSON.parse(loadedFilterOps);
    return parsedJsonbObj;
  }
  return parsedJsonbObj;
};

const MapContent: React.FC<IProps> = ({
  content,
  pageName,
  occurred_min,
  occurred_max,
  jsonb,
  polygon,
  positionSearch,
  zoomSearch,
  polygon_id,
  bbox,
  type,
  apiName,
  registerName,
  loadingMap,
  viewport,
  config,
  fetchTileKeyRequest,
}) => {
  const authHeader = useMemo(() => {
    return { Authorization: `Token ${localStorage.getItem('auth')}` };
  }, []);
  const classes = optionStyles();
  const [drawPolygon, setDrawPolygon] = useState<{}>({});
  const [loading, setLoading] = useState<boolean>(true);
  const { BaseLayer, Overlay } = LayersControl;
  const [tilekey, setTilekey] = useState('');
  const featureRef = useRef<FeatureGroup>(null);
  const layerGroupRef = useRef<LayerGroup>(null);
  const UTFGridLayer = UTFGrid(
    `${process.env.REACT_APP_API_URL}/tiles/table/grout_record/id/${type}/{z}/{x}/{y}.grid.json?tilekey=${tilekey}`,
    {
      resolution: 4,
      pointerCursor: true,
      mouseInterval: 66,
    },
  );
  const [admin, setAdmin] = useState<boolean>(false);
  const [typist, setTypist] = useState<boolean>(false);
  const [tecEng, setTecEng] = useState<boolean>(false);
  const [geofi, setGeofi] = useState<boolean>(false);
  const [publicGroup, setPublicGroup] = useState<boolean>(false);
  const [filterOps, setFilterOps] = useState<
    IncFilterOpsType | SurvFilterOpsType | EngFilterOpsType | EducFilterOpsType
  >(loadFilterStorage(apiName));
  const [clearMap, setClearMap] = useState(true);
  const [snackBar, setSnackbar] = useState<SetSnackbarType>({ open: false, message: '' });
  const [mapViewport, setMapViewport] = useState<Viewport>({ center: [0, 0], zoom: 0 });
  const [lines, setLines] = useState<Array<MapInformation>>([]);
  const [points, setPoints] = useState<Array<MapInformation>>([]);

  useEffect(() => {
    setFilterOps(loadFilterStorage(apiName));

    async function fetchTileKey() {
      setLoading(true);
      
      config.params = {
        ...config.params,
        tilekey: true
      }
      try {
        const response = await fetchTileKeyRequest(config)
        setTilekey(response.data.tilekey)
      } catch {
        setSnackbar({
          open: true,
          message: 'Erro ao carregas os dados. Tente novamente mais tarde.',
        });
      } finally {
        setLoading(false);
      }
    }
    if (!loadingMap) {
      fetchTileKey();
    }
  }, [
    occurred_min,
    occurred_max,
    jsonb,
    authHeader,
    drawPolygon,
    polygon_id,
    type,
    loadingMap,
    apiName,
    filterOps.startDate,
    filterOps.endDate,
  ]);

  const saveStorage = (
    filter: IncFilterOpsType | SurvFilterOpsType | EngFilterOpsType | EducFilterOpsType,
  ) => {
    if (apiName === 'sinistro') {
      localStorage.setItem('incFilterOps', JSON.stringify(filter));
      return;
    }
    if (apiName === 'sinistroSAMU') {
      localStorage.setItem('incSAMUFilterOps', JSON.stringify(filter));
      return;
    }
    if (apiName === 'engenharia') {
      localStorage.setItem('engFilterOps', JSON.stringify(filter));
      return;
    }
    if (apiName === 'fiscalizacao') {
      localStorage.setItem('survFilterOps', JSON.stringify(filter));
    }
    if (apiName === 'educacao') {
      localStorage.setItem('educFilterOps', JSON.stringify(filter));
    }
  };

  function onEdited(e: any) {
    const keyNames = Object.keys(e.layers._layers).join('');
    const coordinates = e.layers._layers[keyNames]._latlngs[0].map((latlng: any) => {
      return [latlng.lng, latlng.lat];
    });
    coordinates.push(coordinates[0]);
    const loadedFilters:
      | IncFilterOpsType
      | EngFilterOpsType
      | SurvFilterOpsType
      | EducFilterOpsType = loadFilterStorage(apiName);

    polygon({
      polygon: {
        type: 'Polygon',
        coordinates: [coordinates],
      },
    });
    setDrawPolygon({
      polygon: {
        type: 'Polygon',
        coordinates: [coordinates],
      },
    });
    setFilterOps(() => {
      const filter: IncFilterOpsType | EngFilterOpsType | SurvFilterOpsType | EducFilterOpsType= {
        ...loadedFilters,
        drawPolygon: {
          polygon: {
            type: 'Polygon',
            coordinates: [coordinates],
          },
        },
      };
      saveStorage(filter);
      setClearMap(false);
      return filter;
    });
  }

  function onCreated(e: any) {
    if (featureRef.current?.leafletElement) {
      const layers = featureRef.current.leafletElement.getLayers();
      if (layers.length > 1) {
        const id = featureRef.current.leafletElement.getLayerId(layers[0]);
        featureRef.current.leafletElement.removeLayer(id);
      }
    }

    const coordinates = e.layer._latlngs[0].map((latlng: any) => {
      return [latlng.lng, latlng.lat];
    });
    coordinates.push(coordinates[0]);

    const loadedFilter = loadFilterStorage(apiName);
    polygon({
      polygon: {
        type: 'Polygon',
        coordinates: [coordinates],
      },
    });
    setDrawPolygon({
      polygon: {
        type: 'Polygon',
        coordinates: [coordinates],
      },
    });

    setFilterOps(() => {
      const filter = {
        ...loadedFilter,
        drawPolygon: {
          polygon: {
            type: 'Polygon',
            coordinates: [coordinates],
          },
        },
      };
      saveStorage(filter);
      setClearMap(false);
      return filter;
    });
  }

  function onDeleted(e: any) {
    const loadedFilters = loadFilterStorage(apiName);

    polygon({});
    setDrawPolygon({});
    setFilterOps(() => {
      const filter: IncFilterOpsType | EngFilterOpsType | SurvFilterOpsType | EducFilterOpsType = {
        ...loadedFilters,
        drawPolygon: {},
      };

      saveStorage(filter);
      setClearMap(true);
      return filter;
    });
  }

  const saveViewportSettings = (value: Viewport) => {
    localStorage.setItem('incMapViewport', JSON.stringify(value));
  };

  // eslint-disable-next-line no-shadow
  function buildLines(data: Array<MapInformation>) {
    const auxLines: Array<MapInformation> = [];
    const auxPoints: Array<MapInformation> = [];
    data.forEach((element) => {
      if (element.geom.type === 'LineString') {
        auxLines.push(element);
      } else {
        auxPoints.push(element);
      }
    });
    console.log(auxPoints)
    setLines(auxLines);
    setPoints(auxPoints);
  }

  useEffect(() => {
    if (clearMap) {
      polygon({});
    }
  }, [polygon, clearMap]);

  useEffect(() => {
    if (viewport) {
      setMapViewport(viewport);
    }
  }, [viewport]);

  useEffect(() => {
    const groups = localStorage.getItem('groups')?.split(' ');
    if (groups) {
      groups.forEach((gr: string) => {
        if (gr === 'public') {
          setPublicGroup(true);
        } else if (gr === 'digitador') {
          setTypist(true);
        } else if (gr === 'tec_engenharia') {
          setTecEng(true);
        } else if (gr === 'geofi') {
          setGeofi(true);
        } else if (gr === 'admin') {
          setAdmin(true);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (content) {
      buildLines(content);
    }
  }, [content]);

  return (
    <Map
      viewport={mapViewport}
      center={
        positionSearch[0] !== 0
          ? [positionSearch[0], positionSearch[1]]
          : POSITION_SEARCH.latlng
      }
      bounds={
        bbox.length !== 0 && bbox[0][0] !== 0
          ? bbox
          : [
            [-3.83137, -38.599091],
            [-3.702559, -38.421936],
          ]
      }
      zoom={viewport?.zoom || (zoomSearch !== 0 ? zoomSearch : 17)}
      onViewportChanged={saveViewportSettings}
    >
      {pageName && (
        <Chip
          title={pageName}
          label={pageName}
          className={`${classes.pageNameChip} ${classes.topPageNameChip}`}
        />
      )}
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={loading}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={loading}>
          <div className={classes.loadingContainer}>
            <CircularProgress
              className={classes.loadingSpinner}
              size={60}
              thickness={5}
            />
          </div>
        </Fade>
      </Modal>
      )
      <LayersControl position="topright">
        <BaseLayer name="Satelite">
          <TileLayer
            zIndex={1}
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
          />
        </BaseLayer>
        <BaseLayer checked name="Ruas">
          <TileLayer
            zIndex={1}
            attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
        </BaseLayer>
        <BaseLayer name="Dark">
          <TileLayer
            zIndex={1}
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
        </BaseLayer>
        <Overlay checked name="Fortaleza">
          <GeoJSON
            data={fortalezaGeoJSON}
            style={{
              color: Colors.main,
              weight: 2,
              fillOpacity: 0.5,
              fillColor: 'transparent',
            }}
          />
        </Overlay>
        {admin && (
          <Overlay name="Câmeras">
            <GeoJSON
              data={fortalezaCansGeoJSON}
              pointToLayer={(geoJsonPoint, latlng) => {
                const iconCam = new L.Icon({ iconUrl: CameraIcon });
                return new L.Marker(latlng, { icon: iconCam }).bindTooltip(
                  geoJsonPoint.properties.id,
                );
              }}
              style={{
                color: Colors.cyan,
                weight: 2,
                fillOpacity: 1,
              }}
            />
          </Overlay>
        )}
        {tilekey ? (
          <Overlay checked name={registerName}>
            <LayerGroup ref={layerGroupRef}>
              <TileLayer
                zIndex={1}
                url={`${process.env.REACT_APP_API_URL}/tiles/table/grout_record/id/${type}/{z}/{x}/{y}.png?tilekey=${tilekey}`}
                onload={() => {
                  UTFGridLayer.addTo(layerGroupRef.current?.leafletElement);
                  UTFGridLayer.on('loading', () => setLoading(true));
                  UTFGridLayer.on('load', () => setLoading(false));
                  UTFGridLayer.on('click', (e: any) =>
                    layerGroupRef.current?.leafletElement &&
                      e.latlng !== undefined &&
                      e.data !== null
                      ? (publicGroup || tecEng || geofi) && !(admin || typist)
                        ? marker(e.latlng, { opacity: 0 })
                          .addTo(layerGroupRef.current?.leafletElement)
                          .bindPopup(
                            `<div style="display:flex; flex-flow:column; justify-content:center, align-items:center;">
                            <div style="width:100%; color:${Colors.main}">
                              <h3 style="font-variant:small-caps; font-weight:bold; font-size:12px">DETALHES DO REGISTRO</h3>
                            </div>
                          <p style="align-self:center; font-weight: bold; font-size: 1rem;">${toDate(
                              e.data.occurred_from,
                            )}</p>
                            <div style="display:flex; justify-content:space-around; width:100%">
                            <a rel="noopener noreferrer" href="/registro/${apiName}/${e.data.uuid
                            }" style="text-decoration:none; width: 40%;" >
                              <button style="width: 100%; padding:5px; font-family:Roboto; font-size:14px; border:transparent; background-color:${Colors.secundary}; color:#404040">EXIBIR</button></a>
                            </div>
                          </div>`,
                          )
                          .on('popupclose', (ev) =>
                            layerGroupRef.current?.leafletElement.removeLayer(
                              ev.target,
                            ),
                          )
                        : marker(e.latlng, { opacity: 0 })
                          .addTo(layerGroupRef.current?.leafletElement)
                          .bindPopup(
                            `<div style="display:flex; flex-flow:column; justify-content:center, align-items:center;">
                                <div style="width:100%; color:${Colors.secundary}>
                                  <h3 style="font-variant:small-caps; font-weight:bold; font-size:12px">DETALHES DO REGISTRO</h3>
                                </div>
                              <p style="align-self:center; font-weight: bold; font-size: 1rem;">${toDate(
                              e.data.occurred_from,
                            )}</p>
                              ${apiName !== 'sinistroSAMU'
                              ? `
                              <div style="display:flex; justify-content:space-around; width:100%">
                              <a rel="noopener noreferrer" href="/editar/${apiName}/${e.data.uuid}/" style="text-decoration:none; width: 40%;">
                                <button style="padding:5px; font-family:Roboto; font-size:14px; width: 100%; border:1px solid ${Colors.secundary}; font-variant:small-caps; color:#404040; background-color:white">EDITAR</button></a>`
                              : ``
                            }
                                  <a rel="noopener noreferrer" href="/registro/${apiName}/${e.data.uuid
                            }" target="_blank" style="text-decoration:none; width: 40%;" >
                                  <button style="width: 100%; padding:5px; font-family:Roboto; font-size:14px; border:transparent; background-color:${Colors.secundary}; color:#404040">EXIBIR</button></a>
                                </div>
                              </div>`,
                          )
                          .on('popupclose', (ev) =>
                            layerGroupRef.current?.leafletElement.removeLayer(
                              ev.target,
                            ),
                          )
                      : null,
                  );
                }}
              />
            </LayerGroup>
          </Overlay>
        ) : null}

        {lines.map((line: MapInformation, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Polyline key={index} color={Colors.main} positions={line.geom.coordinates}>
            <Popup>
              <div className={classes.popupLine}>
                <h3 className={classes.popupTitle}> DETALHES DO REGISTRO: </h3>
                <p className={classes.popupContent}> {toDate(line.occurred_from)} </p>
                <div className={classes.actionButton}>
                  <Link
                    style={{ color: 'black', textAlign: 'center' }}
                    className={classes.linkEditar}
                    to={`/editar/engenharia/${line.uuid}`}
                  >
                    {' '}
                    EDITAR{' '}
                  </Link>
                  <Link
                    style={{ color: 'black', textAlign: 'center' }}
                    className={classes.linkExibir}
                    to={`/registro/engenharia/${line.uuid}`}
                    target="_blank"
                  >
                    {' '}
                    EXIBIR{' '}
                  </Link>
                </div>
              </div>
            </Popup>
          </Polyline>
        ))}
        {/*Essa forma é desenhando o mapa com o Leaflet*/}
        {points.map((point: MapInformation, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <CircleMarker
            key={index}
            fillColor="#E29700"
            color="#E29700"
            fillOpacity={1}
            center={point.geom.coordinates[0]}
            radius={3}
          >
            <Popup>
              <div className={classes.popupLine}>
                <h3 className={classes.popupTitle}> DETALHES DO REGISTRO: </h3>
                <p className={classes.popupContent}> {toDate(point.occurred_from)} </p>
                <div className={classes.actionButton}>
                  <Link
                    style={{ color: 'black', textAlign: 'center' }}
                    className={classes.linkEditar}
                    to={`/editar/engenharia/${point.uuid}`}
                  >
                    {' '}
                    EDITAR{' '}
                  </Link>
                  <Link
                    style={{ color: 'black', textAlign: 'center' }}
                    className={classes.linkExibir}
                    to={`/registro/engenharia/${point.uuid}`}
                    target="_blank"
                  >
                    {' '}
                    EXIBIR{' '}
                  </Link>
                </div>
              </div>
            </Popup>
          </CircleMarker>
        ))}

        <Overlay name="Mapa de calor">
          <TileLayer
            zIndex={1}
            url={`${process.env.REACT_APP_API_URL}/tiles/table/grout_record/id/${type}/{z}/{x}/{y}.png?tilekey=${tilekey}&heatmap=true`}
          />
        </Overlay>

        <Overlay name="Bairros">
          <TileLayer
            zIndex={1}
            url={`${process.env.REACT_APP_API_URL}/tiles/table/grout_boundary/id/${process.env.REACT_APP_NEIGHBORHOOD_BOUNDARY_ID}/{z}/{x}/{y}.png`}
          />
        </Overlay>
        <Overlay checked name="Layer de desenho">
          <FeatureGroup color="purple" ref={featureRef}>
            <EditControl
              position="topleft"
              draw={{
                polyline: false,
                circle: false,
                circlemarker: false,
                marker: false,
              }}
              onEdited={(e: any) => onEdited(e)}
              onCreated={(e: any) => onCreated(e)}
              onDeleted={(e: any) => onDeleted(e)}
            />
          </FeatureGroup>
        </Overlay>
      </LayersControl>
      <ShowSnackbar
        open={snackBar.open}
        message={snackBar.message}
        setSnackbar={setSnackbar}
      />
    </Map>
  );
};
export default MapContent;
